import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CCol } from '@coreui/react-pro'
import Select, { components } from 'react-select'
import { getSettings } from '../../apiHelper/settingsApi'
import { setProcedureModifiers } from '../../redux/dashboard/dashboardSlice'
import { SETTINGS_URL } from '../../apiHelper/apiRoute/settings'
import './PickListCPT.scss'
import _ from 'lodash'
import { favoriteModifier, unfavoriteModifier } from '../../helper/backendHelper'
import { getProvider } from '../../redux/user/userThunk'
import { getTheme } from '../../redux/user/userSlice'
import { BLACK, GRAY_300, GRAY_400, GRAY_700, GRAY_950, WHITE } from '../../scss/colorConsts'

const modSelectStyleDark = {
  clearIndicator: (styles) => ({ ...styles, padding: 0 }),
  container: (styles) => ({ ...styles, width: '100%' }),
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    width: '100%',
  }),
  input: (styles) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
  menu: (styles) => ({ ...styles, width: '12em', fontSize: '0.8em', backgroundColor: GRAY_700 }),
  multiValue: (styles) => ({ ...styles, color: BLACK, backgroundColor: GRAY_400 }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
          ? 'var(--text-glow-color)'
          : isFocused
            ? 'var(--text-glow-color)'
            : 'var(--react-select-side-panel-bg)',
      color: isDisabled ? GRAY_300 : isSelected ? GRAY_950 : isFocused ? GRAY_950 : data?.color,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--reactselect-placeholder-color)',
    fontSize: '12px',
    fontWeight: '400',
  }),
}

// untested unused styles
const modSelectStyleLight = {
  clearIndicator: (styles) => ({ ...styles, padding: 0 }),
  container: (styles) => ({ ...styles, width: '100%' }),
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    width: '100%',
  }),
  input: (styles) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
  menu: (styles) => ({ ...styles, width: '12em', fontSize: '0.8em', backgroundColor: WHITE }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
          ? 'var(--text-glow-color)'
          : isFocused
            ? 'var(--text-glow-color)'
            : 'var(--react-select-side-panel-bg)',
      color: isDisabled ? GRAY_300 : GRAY_950,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--reactselect-placeholder-color)',
    fontSize: '12px',
    fontWeight: '400',
  }),
}

const PickListCPT = ({ item, setSelectedProcedureModifiers, selectedProcedureModifiers }) => {
  const dispatch = useDispatch()
  const theme = useSelector(getTheme)
  const procedure_modifiers = useSelector((state) => state?.dashboard?.procedure_modifiers)
  const provider = useSelector((state) => state.user?.provider)
  const favoritedModifiers = provider?.fav_modifiers || []

  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const getModifiersList = async () => {
      let res = await getSettings(SETTINGS_URL.GET_PICKLIST)
      if (res?.status === 'success') {
        let newOptions = res?.results?.map((item) => ({
          label: item?.modifier + ' ' + item?.description,
          value: item?.modifier,
        }))
        dispatch(setProcedureModifiers(newOptions))
      }
    }
    if (procedure_modifiers.length === 0) {
      getModifiersList()
    }
  }, [dispatch, procedure_modifiers])

  let procedureModsWithSearchTitle = []
  const procedureModsWithoutSearchTitle = []

  // allow search results to be filtered by title/value relevance
  procedure_modifiers.forEach((mod) => {
    if (mod.value.toLowerCase().includes(searchTerm.toLowerCase())) {
      procedureModsWithSearchTitle.push(mod)
    } else {
      procedureModsWithoutSearchTitle.push(mod)
    }
  })

  procedureModsWithSearchTitle = procedureModsWithSearchTitle.sort((a, b) => {
    // + converts the bool to a 1 or 0
    const aIsFav = +favoritedModifiers.includes(a.value)
    const bIsFav = +favoritedModifiers.includes(b.value)

    return bIsFav - aIsFav
  })

  const debounceModifierFavoriting = _.debounce(async (setFavorite, mod) => {
    setFavorite ? await favoriteModifier(mod) : await unfavoriteModifier(mod)
    await dispatch(getProvider())
  }, 700)

  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', wordBreak: 'break-word' }}>
          {favoritedModifiers.includes(props.value) ? (
            <i
              className={`fa-solid fa-star ${props.isFocused ? 'fav-icon-gold' : 'fav-icon'}`}
              style={{ fontSize: '1.5em', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                debounceModifierFavoriting(false, props.value)
              }}
            ></i>
          ) : (
            <i
              className="fa-regular fa-star"
              style={{ fontSize: '1.5em', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                debounceModifierFavoriting(true, props.value)
              }}
            ></i>
          )}
          {props.label}
        </div>
      </components.Option>
    )
  }

  return (
    <CCol className="d-flex modifier-picklist-width pick-list-cpt" style={{ width: '100%' }}>
      <Select
        components={{ Option: CustomOption }}
        isMulti
        closeMenuOnSelect={false}
        isOptionDisabled={() => selectedProcedureModifiers.length >= 4}
        options={[...procedureModsWithSearchTitle, ...procedureModsWithoutSearchTitle]}
        classNamePrefix={'CPTPicker'}
        styles={theme === 'light' ? modSelectStyleLight : modSelectStyleDark}
        placeholder={''}
        classNames={{
          menu: () => 'pickListMenu',
        }}
        onChange={(data) => {
          let reformattedModifiers = data.map((datum) => datum?.value)
          setSelectedProcedureModifiers(reformattedModifiers)
        }}
        value={
          procedure_modifiers
            .filter((mod) => selectedProcedureModifiers.includes(mod.value))
            .map((mod) => ({ ...mod, label: mod.label.slice(0, 2) })) || []
        }
        onInputChange={(currentSearchTerm) => setSearchTerm(currentSearchTerm)}
      />
    </CCol>
  )
}

export default PickListCPT
